/*!
 * AMOUR amour.si — /cart/add interceptor for inspired-by line-item property
 *
 * The hidden <input> rendered by snippets/inspired-cart-property.liquid
 * already covers add-to-cart paths that go through the standard product
 * form (native submits + theme.js AjaxProduct, both of which serialise
 * every named input). This file augments the remaining JS-driven paths
 * that build their /cart/add request body manually:
 *
 *   - snippets/sticky-add-to-cart.liquid
 *       Sends `{"id":<variant>,"quantity":<n>}` with no properties.
 *   - Qikify upsells / "Add" buttons
 *       Same shape, occasionally a bulk add `{"items":[…]}`.
 *   - Any other future quick-add surface (collection cards, etc.).
 *
 * Logic:
 *   1. Read window.amourInspiredProductData (set by the Liquid snippet on
 *      the current product page; absent on non-product pages or for
 *      products without an `inspired_by` metafield).
 *   2. Hook fetch + XMLHttpRequest. For requests targeting /cart/add(.js)
 *      whose variant id is one of the page's product variants, inject
 *      `properties["<label>"] = "<value>"` into the body if absent.
 *   3. Idempotent: never overwrites an already-present property and is
 *      safe to call multiple times.
 *
 * Host guard is at the layout level (only loaded on amour.si and the
 * amour-parfums-2 preview host). Property KEY/VALUE come straight from
 * the snippet so this file is store-agnostic — drop in as is on any
 * Amour storefront and it just works.
 */
(function () {
  'use strict';
  if (window.__amourInspiredCartPropertyHooked) return;
  window.__amourInspiredCartPropertyHooked = true;

  var ADD_ENDPOINT_RE = /\/cart\/add(\.js)?(\?|$)/i;

  function getData() {
    var d = window.amourInspiredProductData;
    if (!d || !d.label || !d.value || !Array.isArray(d.variants)) return null;
    return d;
  }

  function variantMatches(data, id) {
    if (id === undefined || id === null || id === '') return false;
    var n = typeof id === 'number' ? id : parseInt(String(id), 10);
    if (!isFinite(n)) return false;
    return data.variants.indexOf(n) !== -1;
  }

  function ensureProperty(propertiesObj, data) {
    var key = data.label;
    if (propertiesObj && Object.prototype.hasOwnProperty.call(propertiesObj, key)) {
      return propertiesObj;
    }
    propertiesObj = propertiesObj || {};
    propertiesObj[key] = data.value;
    return propertiesObj;
  }

  /**
   * Augment a JSON-shaped /cart/add body. Handles both:
   *   { id: <variant>, quantity: <n>, properties?: {…} }
   *   { items: [{ id, quantity, properties? }, …] }
   * Returns a (possibly new) object; caller stringifies.
   */
  function augmentJsonBody(body, data) {
    if (!body || typeof body !== 'object') return body;

    if (Array.isArray(body.items)) {
      body.items = body.items.map(function (item) {
        if (item && variantMatches(data, item.id)) {
          item.properties = ensureProperty(item.properties, data);
        }
        return item;
      });
      return body;
    }

    if (variantMatches(data, body.id)) {
      body.properties = ensureProperty(body.properties, data);
    }
    return body;
  }

  /**
   * Augment a urlencoded body string. Mutates a URLSearchParams
   * representation and returns a new urlencoded string. Skips work if
   * the property is already present or the variant doesn't belong to
   * the current product.
   */
  function augmentUrlEncodedBody(bodyStr, data) {
    var params;
    try {
      params = new URLSearchParams(bodyStr);
    } catch (_) {
      return bodyStr;
    }
    var id = params.get('id');
    if (!variantMatches(data, id)) return bodyStr;

    var propKey = 'properties[' + data.label + ']';
    if (params.has(propKey)) return bodyStr;

    params.append(propKey, data.value);
    return params.toString();
  }

  function isJsonContentType(ct) {
    return typeof ct === 'string' && ct.toLowerCase().indexOf('application/json') !== -1;
  }

  function getHeader(headers, name) {
    if (!headers) return '';
    if (typeof headers.get === 'function') {
      try { return headers.get(name) || ''; } catch (_) { return ''; }
    }
    if (typeof headers === 'object') {
      var lower = name.toLowerCase();
      for (var k in headers) {
        if (Object.prototype.hasOwnProperty.call(headers, k) && k.toLowerCase() === lower) {
          return headers[k] || '';
        }
      }
    }
    return '';
  }

  // ---------- fetch hook ----------
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var data = getData();
        if (!data) return origFetch.apply(this, arguments);

        var url = typeof input === 'string'
          ? input
          : (input && (input.url || input.href)) || '';
        if (!ADD_ENDPOINT_RE.test(url)) return origFetch.apply(this, arguments);

        if (init && init.body !== undefined && init.body !== null) {
          var ct = getHeader(init.headers, 'Content-Type');
          var body = init.body;

          if (typeof body === 'string') {
            var trimmed = body.trim();
            if (trimmed.charAt(0) === '{' || isJsonContentType(ct)) {
              try {
                var parsed = JSON.parse(body);
                var augmented = augmentJsonBody(parsed, data);
                init = Object.assign({}, init, { body: JSON.stringify(augmented) });
              } catch (_) { /* leave original body */ }
            } else {
              init = Object.assign({}, init, { body: augmentUrlEncodedBody(body, data) });
            }
          } else if (body instanceof FormData) {
            var fdId = body.get('id');
            if (variantMatches(data, fdId)) {
              var fdKey = 'properties[' + data.label + ']';
              if (!body.has(fdKey)) body.append(fdKey, data.value);
            }
          } else if (body instanceof URLSearchParams) {
            var spId = body.get('id');
            if (variantMatches(data, spId)) {
              var spKey = 'properties[' + data.label + ']';
              if (!body.has(spKey)) body.append(spKey, data.value);
            }
          }
        }
        return origFetch.call(this, input, init);
      } catch (_) {
        return origFetch.apply(this, arguments);
      }
    };
  }

  // ---------- XHR hook ----------
  var XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    var origOpen = XHR.prototype.open;
    var origSend = XHR.prototype.send;

    XHR.prototype.open = function (method, url) {
      try {
        this.__amourInspiredAddUrl = url || '';
      } catch (_) {}
      return origOpen.apply(this, arguments);
    };

    XHR.prototype.send = function (body) {
      try {
        var data = getData();
        var url = this.__amourInspiredAddUrl || '';
        if (data && ADD_ENDPOINT_RE.test(url) && body !== undefined && body !== null) {
          if (typeof body === 'string') {
            var trimmed = body.trim();
            if (trimmed.charAt(0) === '{') {
              try {
                var parsed = JSON.parse(body);
                body = JSON.stringify(augmentJsonBody(parsed, data));
              } catch (_) { /* leave original body */ }
            } else {
              body = augmentUrlEncodedBody(body, data);
            }
          } else if (body instanceof FormData) {
            var fdId = body.get('id');
            if (variantMatches(data, fdId)) {
              var fdKey = 'properties[' + data.label + ']';
              if (!body.has(fdKey)) body.append(fdKey, data.value);
            }
          } else if (body instanceof URLSearchParams) {
            var spId = body.get('id');
            if (variantMatches(data, spId)) {
              var spKey = 'properties[' + data.label + ']';
              if (!body.has(spKey)) body.append(spKey, data.value);
            }
          }
        }
      } catch (_) { /* never throw into the page */ }
      return origSend.call(this, body);
    };
  }
})();
